import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, Zap, Rocket, Building2, Globe, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const PLANS = [
  {
    id: "free",
    name: "Free",
    description: "Try SyncChain with basic access",
    monthlyPrice: 0,
    annualPrice: 0,
    credits: "3 searches",
    icon: Zap,
    color: "text-gray-400",
    borderColor: "border-white/10",
    features: [
      "3 single song searches",
      "Basic recording metadata",
      "Writer & publisher identification",
    ],
    limitations: [
      "No catalogue import",
      "No batch CSV upload",
      "No CSV/JSON export",
    ],
    cta: "Get Started Free",
    popular: false,
  },
  {
    id: "professional",
    name: "Professional",
    description: "For music supervisors, producers, indie labels",
    monthlyPrice: 25,
    annualPrice: 20,
    credits: "25 credits/month",
    icon: Sparkles,
    color: "text-syncchain-accent",
    borderColor: "border-syncchain-accent/40",
    features: [
      "25 credits per month",
      "Full rights resolution per credit",
      "Writers, publishers & labels",
      "Performing & mechanical splits",
      "Limited contact info",
      "Single song search",
    ],
    limitations: [],
    overage: "$1.50 per additional credit",
    cta: "Start Professional",
    popular: false,
  },
  {
    id: "pro_plus",
    name: "Pro+",
    description: "For agencies, sync teams, catalogue managers",
    monthlyPrice: 75,
    annualPrice: 60,
    credits: "100 credits/month",
    icon: Rocket,
    color: "text-purple-400",
    borderColor: "border-purple-500/40",
    features: [
      "100 credits per month",
      "Full rights breakdown with % splits",
      "Contact pathways (email / reps)",
      "CSV & JSON export",
      "Catalogue import from Spotify",
      "Batch CSV upload",
      "Saved searches & shortlists",
    ],
    limitations: [],
    overage: "$1.00 per additional credit",
    cta: "Start Pro+",
    popular: true,
  },
  {
    id: "business",
    name: "Business",
    description: "For brands, production companies, mid-size platforms",
    monthlyPrice: 250,
    annualPrice: 200,
    credits: "500 credits/month",
    icon: Building2,
    color: "text-blue-400",
    borderColor: "border-blue-500/40",
    features: [
      "500–1,500 credits per month",
      "Multi-user access (3–10 seats)",
      "Priority data accuracy",
      "Bulk search tools",
      "API-lite access (limited endpoints)",
      "Usage analytics dashboard",
      "All Pro+ features included",
    ],
    limitations: [],
    overage: "$0.75 per credit",
    cta: "Start Business",
    popular: false,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    description: "For major platforms, DSPs, labels, broadcasters",
    monthlyPrice: null,
    annualPrice: null,
    credits: "10k–100k+ credits/month",
    icon: Globe,
    color: "text-emerald-400",
    borderColor: "border-emerald-500/40",
    features: [
      "Custom credit volume",
      "Full API access",
      "SLA & uptime guarantees",
      "Dedicated support",
      "Custom data integrations",
      "White-label / embedded workflows",
      "All Business features included",
    ],
    limitations: [],
    overage: "Custom ($0.25–$0.60 per resolution)",
    cta: "Contact Sales",
    popular: false,
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);
  const navigate = useNavigate();

  const handleSelectPlan = (plan) => {
    if (plan.id === "free") {
      navigate("/register");
    } else if (plan.id === "enterprise") {
      window.location.href = "mailto:hello@syncit.ac?subject=SyncChain Enterprise Inquiry";
    } else {
      navigate(`/checkout?plan=${plan.id}&billing=${annual ? "annual" : "monthly"}`);
    }
  };

  return (
    <div className="min-h-screen bg-syncchain-bg text-syncchain-text" data-testid="pricing-page">
      {/* Nav */}
      <nav className="border-b border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_87e61a64-90d2-4c20-8ba6-31392dc5e379/artifacts/xzzkl8ic_SyncChain_White.png"
              alt="SyncChain"
              className="h-8"
            />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm text-syncchain-text-secondary hover:text-syncchain-text transition-colors">
              Sign In
            </Link>
            <Button asChild size="sm" className="bg-syncchain-accent hover:bg-syncchain-accent-hover text-white rounded-full px-5">
              <Link to="/register">Get Started</Link>
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="text-center pt-16 pb-12 px-6">
        <Badge variant="outline" className="mb-4 text-syncchain-accent border-syncchain-accent/30 bg-syncchain-accent/5 px-3 py-1">
          Rights Intelligence Platform
        </Badge>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl mx-auto leading-tight">
          Resolve music ownership<br />
          <span className="text-syncchain-accent">in seconds, not hours</span>
        </h1>
        <p className="text-syncchain-text-secondary mt-4 max-w-xl mx-auto text-base">
          1 credit = 1 verified rights resolution. Writers, publishers, labels, ownership splits, and contact routes — all in one search.
        </p>
        <p className="text-xs text-syncchain-text-muted mt-3">
          We don't charge credits for empty or failed searches.
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-3 mb-12" data-testid="billing-toggle">
        <span className={`text-sm font-medium ${!annual ? "text-syncchain-text" : "text-syncchain-text-muted"}`}>Monthly</span>
        <button
          onClick={() => setAnnual(!annual)}
          className={`relative w-14 h-7 rounded-full transition-colors ${annual ? "bg-syncchain-accent" : "bg-white/20"}`}
          data-testid="billing-switch"
        >
          <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${annual ? "left-8" : "left-1"}`} />
        </button>
        <span className={`text-sm font-medium ${annual ? "text-syncchain-text" : "text-syncchain-text-muted"}`}>
          Annual
          <span className="ml-1.5 text-xs text-emerald-400 font-semibold">Save 20%</span>
        </span>
      </div>

      {/* Pricing Grid */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl border ${plan.borderColor} bg-syncchain-paper p-6 flex flex-col transition-all hover:border-syncchain-accent/60 hover:bg-syncchain-paper/80 ${
                plan.popular ? "ring-2 ring-syncchain-accent/50" : ""
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-syncchain-accent text-white px-3 py-0.5 text-xs font-semibold">
                    Most Popular
                  </Badge>
                </div>
              )}

              <div className="mb-4">
                <plan.icon className={`w-6 h-6 ${plan.color} mb-3`} />
                <h3 className="text-lg font-bold text-syncchain-text">{plan.name}</h3>
                <p className="text-xs text-syncchain-text-muted mt-1 leading-relaxed">{plan.description}</p>
              </div>

              <div className="mb-5">
                {plan.monthlyPrice !== null ? (
                  <>
                    <span className="text-3xl font-bold text-syncchain-text">
                      ${annual ? plan.annualPrice : plan.monthlyPrice}
                    </span>
                    <span className="text-syncchain-text-muted text-sm">/mo</span>
                    {annual && plan.monthlyPrice > 0 && (
                      <p className="text-xs text-syncchain-text-muted mt-1 line-through">${plan.monthlyPrice}/mo</p>
                    )}
                  </>
                ) : (
                  <span className="text-2xl font-bold text-syncchain-text">Custom</span>
                )}
                <p className="text-xs text-syncchain-accent font-medium mt-2">{plan.credits}</p>
              </div>

              <div className="flex-1 space-y-2.5 mb-6">
                {plan.features.map((f, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-syncchain-text-secondary leading-relaxed">{f}</span>
                  </div>
                ))}
                {plan.limitations.map((f, i) => (
                  <div key={i} className="flex items-start gap-2 opacity-50">
                    <span className="w-3.5 h-3.5 text-center text-red-400 text-xs flex-shrink-0">-</span>
                    <span className="text-xs text-syncchain-text-muted leading-relaxed">{f}</span>
                  </div>
                ))}
              </div>

              {plan.overage && (
                <p className="text-[10px] text-syncchain-text-muted mb-4 border-t border-white/5 pt-3">
                  Overage: {plan.overage}
                </p>
              )}

              <Button
                onClick={() => handleSelectPlan(plan)}
                className={`w-full rounded-full text-sm font-semibold ${
                  plan.popular
                    ? "bg-syncchain-accent hover:bg-syncchain-accent-hover text-white"
                    : plan.id === "free"
                    ? "bg-white/10 hover:bg-white/15 text-syncchain-text"
                    : "bg-white/5 hover:bg-white/10 text-syncchain-text border border-white/10"
                }`}
                data-testid={`select-plan-${plan.id}`}
              >
                {plan.cta}
                <ArrowRight className="w-3.5 h-3.5 ml-2" />
              </Button>
            </div>
          ))}
        </div>

        {/* Credit Explanation */}
        <div className="mt-16 max-w-2xl mx-auto text-center">
          <h3 className="text-lg font-bold text-syncchain-text mb-4">How Credits Work</h3>
          <div className="bg-syncchain-surface rounded-xl border border-white/10 p-6 text-left">
            <p className="text-sm text-syncchain-text-secondary mb-3">
              <strong className="text-syncchain-text">1 credit = 1 verified rights resolution.</strong> A resolution includes:
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs text-syncchain-text-secondary">
              <div className="flex items-center gap-2"><Check className="w-3 h-3 text-emerald-400" /> Writers</div>
              <div className="flex items-center gap-2"><Check className="w-3 h-3 text-emerald-400" /> Publishers</div>
              <div className="flex items-center gap-2"><Check className="w-3 h-3 text-emerald-400" /> Label (if applicable)</div>
              <div className="flex items-center gap-2"><Check className="w-3 h-3 text-emerald-400" /> Ownership %</div>
              <div className="flex items-center gap-2"><Check className="w-3 h-3 text-emerald-400" /> Contact route (where available)</div>
              <div className="flex items-center gap-2"><Check className="w-3 h-3 text-emerald-400" /> Society affiliations</div>
            </div>
            <p className="text-xs text-syncchain-text-muted mt-4 border-t border-white/5 pt-3">
              Failed or empty searches are not charged. You only pay for successful resolutions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
